import { type MessageDescriptor, defineMessage } from 'react-intl';
import { type RankerApi } from 'pages/api/ranker';
import { type ReportDemosPlusPodcastAu, type ReportDemosPlusPodcastUs, type DemosPlusRow } from 'report/getReport';
import { type TableColumn, getDemoPlusColumnHeader, getDemosPlusCharacteristicColumn } from 'report/tableColumns';
import { type ReportType } from 'report/types';

export enum DemosPlusColumnKey {
    CHARACTERISTICS = 'characteristics',
}
// ===================================================
// ================= DEMO + UNITED STATES ============
// ===================================================

// Characteristic group definitions based on PCM Audience structure
export const UsDemosPlusCharacteristicGroups = {
    demographic: [
        'adults_18_24',
        'adults_18_34',
        'adults_35_54',
        'adults_55_plus',
        'female_18_plus',
        'female_18_24',
        'female_18_34',
        'female_35_54',
        'female_55_plus',
        'male_18_plus',
        'male_18_24',
        'male_18_34',
        'male_35_54',
        'male_55_plus',
    ],
    advancedDemographic: [
        'college_grad_plus',
        'female_25_54_and_parent_wchild_less_18',
        'hhi_100k_plus',
        'hhi_150k_plus',
        'hispanic',
        'inv_assets_250k+',
        'parent_wchild_less_18',
        'politics_democ_lean',
        'politics_repub_lean',
        'post_grad',
        'race_black_aa',
        'senior_decision_maker',
        'small_business_owner',
    ],
    purchaseLifestyle: [
        'qsr_weekly',
        'business_travel_intent',
        'leisure_travel_intent',
        'switch_wireless_provider_intent',
        'life_insurance_intent',
        'home_auto_insurance_intent',
        'buy_new_car_truck_intent',
        'home_improvement_intent',
        'cinema_monthly',
        'exercise_gym_pm',
        'supplements_vitamins_pm',
        'weightloss_products_pm',
        'exercise_home_pm',
        'department_store_pm',
        'bigbox_store_pm',
        'fastfashion_store_pm',
        'luxury_boutique_pm',
        'online_shopper',
        'inperson_shopper',
    ],
} as const;

// US Characteristic Groups for PCM Audience structure
export type UsDemosPlusGroups = keyof typeof UsDemosPlusCharacteristicGroups;

export const UsDemosPlusHeaders = [
    ...UsDemosPlusCharacteristicGroups.demographic,
    ...UsDemosPlusCharacteristicGroups.advancedDemographic,
    ...UsDemosPlusCharacteristicGroups.purchaseLifestyle,
] as const;

export type UsDemoPlusCharacteristicHeader = (typeof UsDemosPlusHeaders)[number];
export type UsDemosPlusCsvCharacteristic = Partial<Record<UsDemoPlusCharacteristicHeader, number>>;

// ===================================================
// ================= DEMO + AUSTRALIA ============
// ===================================================
export const AuDemosPlusCharacteristicGroups = {
    demographic: [
        'adults_21_plus',
        'adults_18_24',
        'adults_18_34',
        'adults_18_39',
        'adults_18_49',
        'adults_25_34',
        'adults_25_39',
        'adults_25_54',
        'adults_35_44',
        'adults_35_54',
        'adults_35_64',
        'adults_40_54',
        'adults_45_54',
        'adults_55_64',
        'adults_55_plus',
        'adults_65_plus',
        'female_18_plus',
        'female_21_plus',
        'female_18_24',
        'female_18_34',
        'female_18_39',
        'female_18_49',
        'female_25_34',
        'female_25_39',
        'female_25_54',
        'female_35_44',
        'female_35_54',
        'female_35_64',
        'female_40_54',
        'female_45_54',
        'female_55_64',
        'female_55_plus',
        'female_65_plus',
        'male_18_plus',
        'male_21_plus',
        'male_18_24',
        'male_18_34',
        'male_18_39',
        'male_18_49',
        'male_25_34',
        'male_25_39',
        'male_25_54',
        'male_35_44',
        'male_35_54',
        'male_35_64',
        'male_40_54',
        'male_45_54',
        'male_55_64',
        'male_55_plus',
        'male_65_plus',
    ],
    advancedDemographic: [
        'parent_wchild_less_18',
        'mom_wchild_less_18',
        'hhi_0_50k',
        'hhi_50k_70k',
        'hhi_70_100k',
        'hhi_100k_plus',
        'hhi_100_150k',
        'hhi_150_200k',
        'hhi_150k_plus',
        'hhi_200k_plus',
        'married',
        'single',
        'empl_fulltime',
        'empl_homeduties_fulltimeparent_caregiver',
        'empl_manager_administrator',
        'empl_paraprofessional(e.g._nurse_educationalaide_paralegal_bookkeeper)',
        'empl_professional(e.g._doctor_lawyer_professor_certifiedteacher_accountant)',
        'empl_casual_part_time',
        'empl_trades_labourers',
        'small_medium_business_owner',
        'senior_decision_maker',
        'student_apprentice',
        'sports_avidfan',
        'afl_avidfan',
        'nrl_avidfan',
        'horse_racing_avidfan',
        'cricket_avidfan',
        'netball_avidfan',
        'basketball_avidfan',
        'motorsports_avidfan',
        'golf_avidfan',
        'tennis_avidfan',
        'nfl_avidfan',
        'soccer_avidfan',
        'olympics_avidfan',
        'cooking_p2m',
        'gardening_p2m',
        'painting_p2m',
        'photography_p2m',
        'home_renovations_p2m',
        'reading_p2m',
        'cars_p2m',
        'fitness_p2m',
        'new_tech_p2m',
        'arts_p2m',
        'outdoor_p2m',
        'outdoor_water_p2m',
    ],
    media: [
        'podcasts_<weekly',
        'podcasts_<5hrs_wk',
        'podcasts_5+hrs_wk',
        'first_listen_past_year',
        'first_listen_past_2_5_yrs',
        'first_listen_past_5_yrs_plus',
        'newspapers_pm',
        'magazines_pm',
        'radio_streamed_pm',
        'music_streaming_pm',
        'audio_books_pm',
        'newspapers_pw',
        'magazines_pw',
        'am_fm_dab_radio_pw',
        'radio_streamed_pw',
        'you_tube_pw',
        'tv_commercial_broadcast_pw',
        'tv_abc_sbs_pw',
        'tv_catch_up_pw',
        'pay_tv_pw',
        'music_streaming_pw',
        'social_media_pw',
        'online_news_pw',
        'audio_books_pw',
        'non_listener_am_fm_dab_radio_pm',
    ],
    purchase: [
        'purchase_sell_home_ny',
        'purchase_investment_prop_ny',
        'switch_open_bank_account_ny',
        'finance_refinance_mortgage_ny',
        'financial_advisor_ny',
        'own_home_no_mortage',
        'own_home_w_mortage',
        'rent_home',
        'chemist_pw',
        'groceries_pw',
        'take_away_store_pw',
        'take_away_app_pw',
        'casual_dining_pw',
        'fine_dining_pw',
        'meal_kit_pw',
        'primary_grocery_shopper',
        'hardware_store_pm',
        'garden_nursery_pm',
        'clothing_fashion_pm',
        'furniture_pm',
        'alcohol_pm',
        'anything_online_pm',
        'own_car_truck',
        'have_health_insurance',
        'have_credit_card',
        'have_vehicle_insurance',
        'have_home_insurance',
        'have_pet',
        'video_streaming_subs',
        'music_streaming_subs',
        'music_streaming_free',
        'whitegoods_appliances_ny',
        'ac_heater_ny',
        'furniture_ny',
        'audio_visual_eqpmnt_ny',
        'holi_travel_dom_ny',
        'holi_travel_int_ny',
        'bus_travel_dom_ny',
        'bus_travel_int_ny',
        'gym_memb_eqpmnt_ny',
        'home_renos_ny',
        'motor_vehicle_ny',
        'home_tech_ny',
        'mobile_phone_ny',
        'new_internet_provider_ny',
        'new_mobile_provider_ny',
        'new_energy_provider_ny',
        'new_health_insurance_ny',
        'new_car_insurance_ny',
        'qsr_daily',
        'qsr_weekly',
        'cinema_heavy',
        'cinema_medium',
        'cinema_light',
        'primary_grocery_shopper_w_child',
        'in_person_shoppers',
        'online_shoppers',
        'in_person_online_shoppers',
    ],
} as const;

export const AuDemosPlusHeaders = [
    ...AuDemosPlusCharacteristicGroups.demographic,
    ...AuDemosPlusCharacteristicGroups.advancedDemographic,
    ...AuDemosPlusCharacteristicGroups.media,
    ...AuDemosPlusCharacteristicGroups.purchase,
] as const;

export type AuDemoPlusCharacteristicHeader = (typeof AuDemosPlusHeaders)[number];
export type AuDemosPlusCsvCharacteristic = Partial<Record<AuDemoPlusCharacteristicHeader, number>>;
export type AuDemosPlusGroups = keyof typeof AuDemosPlusCharacteristicGroups;

// ===================================================
// ========= DEMO + AUSTRALIA PROGRAM ================
// ===================================================
// AU Program report uses purchase/lifestyle characteristics
// (different from AU Podcast which uses demographic characteristics)
export const AuDemosPlusProgramCharacteristicGroups = {
    purchaseLifestyle: [
        'ac_heater_ny',
        'alcohol_pm',
        'anything_online_pm',
        'audio_visual_eqpmnt_ny',
        'bus_travel_dom_ny',
        'bus_travel_int_ny',
        'casual_dining_pw',
        'chemist_pw',
        'cinema_heavy',
        'cinema_light',
        'cinema_medium',
        'clothing_fashion_pm',
        'empl_fulltime',
        'finance_refinance_mortgage_ny',
        'fine_dining_pw',
        'fitness_p2m',
        'furniture_ny',
        'furniture_pm',
        'garden_nursery_pm',
        'gardening_p2m',
        'groceries_pw',
        'gym_memb_eqpmnt_ny',
        'hardware_store_pm',
        'have_credit_card',
        'have_health_insurance',
        'have_home_insurance',
        'have_pet',
        'have_vehicle_insurance',
        'hhi_150k_plus',
        'holi_travel_dom_ny',
        'holi_travel_int_ny',
        'home_renos_ny',
        'home_renovations_p2m',
        'home_tech_ny',
        'in_person_online_shoppers',
        'in_person_shoppers',
        'married',
        'meal_kit_pw',
        'mobile_phone_ny',
        'motor_vehicle_ny',
        'music_streaming_free',
        'music_streaming_subs',
        'new_car_insurance_ny',
        'new_energy_provider_ny',
        'new_health_insurance_ny',
        'new_internet_provider_ny',
        'new_mobile_provider_ny',
        'online_shoppers',
        'own_car_truck',
        'own_home_no_mortage',
        'own_home_w_mortage',
        'parent_wchild_less_18',
        'primary_grocery_shopper',
        'primary_grocery_shopper_w_child',
        'purchase_sell_home_ny',
        'qsr_daily',
        'qsr_weekly',
        'rent_home',
        'single',
        'small_medium_business_owner',
        'sports_avidfan',
        'switch_open_bank_account_ny',
        'take_away_app_pw',
        'take_away_store_pw',
        'video_streaming_subs',
        'whitegoods_appliances_ny',
    ],
} as const;

export type AuDemosPlusProgramGroups = keyof typeof AuDemosPlusProgramCharacteristicGroups;

export const AuDemosPlusProgramHeaders = [...AuDemosPlusProgramCharacteristicGroups.purchaseLifestyle] as const;

export type AuDemoPlusProgramCharacteristicHeader = (typeof AuDemosPlusProgramHeaders)[number];

// ===================================================
// ======== DEMO + CHARACTERISTIC GROUPS =============
// ===================================================

export const DemosPlusGroupMessageMap: Record<
    UsDemosPlusGroups | AuDemosPlusGroups | AuDemosPlusProgramGroups,
    MessageDescriptor
> = {
    demographic: defineMessage({
        id: 'demosPlusGroupsUs.demographics',
        defaultMessage: 'Demographics',
        description: 'Group name for demographic characteristics',
    }),
    advancedDemographic: defineMessage({
        id: 'demosPlusGroupsUs.advancedDemos',
        defaultMessage: 'Advanced demographics',
        description: 'Group name for advanced demographic characteristics',
    }),
    purchaseLifestyle: defineMessage({
        id: 'demosPlusGroupsUs.purchaseLifestyle',
        defaultMessage: 'Purchase/lifestyle',
        description: 'Group name for purchase and lifestyle characteristics',
    }),
    media: defineMessage({
        id: 'demosPlusGroupsAu.media',
        defaultMessage: 'Media',
        description: 'Group name for media characteristics [Australia only]',
    }),
    purchase: defineMessage({
        id: 'demosPlusGroupsAu.purchase',
        defaultMessage: 'Purchase',
        description: 'Group name for purchase characteristics [Australia only]',
    }),
};

type HeaderMessageMap = {
    [K in
        | AuDemoPlusCharacteristicHeader
        | UsDemoPlusCharacteristicHeader
        | AuDemoPlusProgramCharacteristicHeader]: MessageDescriptor;
};

// ===================================================
// ======== DEMO + CHARACTERISTIC HEADERS ============
// ===================================================
export const DemosPlusHeaderMessageMap: HeaderMessageMap = {
    // ===================================================
    // ========== AU Podcast Demographics =================
    // ===================================================
    adults_21_plus: defineMessage({
        id: 'adults_21_plus',
        defaultMessage: 'Adults 21+',
    }),
    adults_18_24: defineMessage({
        id: 'adults_18_24',
        defaultMessage: 'Adults 18-24',
    }),
    adults_18_34: defineMessage({
        id: 'adults_18_34',
        defaultMessage: 'Adults 18-34',
    }),
    adults_18_39: defineMessage({
        id: 'adults_18_39',
        defaultMessage: 'Adults 18-39',
    }),
    adults_18_49: defineMessage({
        id: 'adults_18_49',
        defaultMessage: 'Adults 18-49',
    }),
    adults_25_34: defineMessage({
        id: 'adults_25_34',
        defaultMessage: 'Adults 25-34',
    }),
    adults_25_39: defineMessage({
        id: 'adults_25_39',
        defaultMessage: 'Adults 25-39',
    }),
    adults_25_54: defineMessage({
        id: 'adults_25_54',
        defaultMessage: 'Adults 25-54',
    }),
    adults_35_44: defineMessage({
        id: 'adults_35_44',
        defaultMessage: 'Adults 35-44',
    }),
    adults_35_54: defineMessage({
        id: 'adults_35_54',
        defaultMessage: 'Adults 35-54',
    }),
    adults_35_64: defineMessage({
        id: 'adults_35_64',
        defaultMessage: 'Adults 35-64',
    }),
    adults_40_54: defineMessage({
        id: 'adults_40_54',
        defaultMessage: 'Adults 40-54',
    }),
    adults_45_54: defineMessage({
        id: 'adults_45_54',
        defaultMessage: 'Adults 45-54',
    }),
    adults_55_64: defineMessage({
        id: 'adults_55_64',
        defaultMessage: 'Adults 55-64',
    }),
    adults_55_plus: defineMessage({
        id: 'adults_55_plus',
        defaultMessage: 'Adults 55+',
    }),
    adults_65_plus: defineMessage({
        id: 'adults_65_plus',
        defaultMessage: 'Adults 65+',
    }),
    female_18_plus: defineMessage({
        id: 'female_18_plus',
        defaultMessage: 'Female 18+',
    }),
    female_21_plus: defineMessage({
        id: 'female_21_plus',
        defaultMessage: 'Female 21+',
    }),
    female_18_24: defineMessage({
        id: 'female_18_24',
        defaultMessage: 'Female 18-24',
    }),
    female_18_34: defineMessage({
        id: 'female_18_34',
        defaultMessage: 'Female 18-34',
    }),
    female_18_39: defineMessage({
        id: 'female_18_39',
        defaultMessage: 'Female 18-39',
    }),
    female_18_49: defineMessage({
        id: 'female_18_49',
        defaultMessage: 'Female 18-49',
    }),
    female_25_34: defineMessage({
        id: 'female_25_34',
        defaultMessage: 'Female 25-34',
    }),
    female_25_39: defineMessage({
        id: 'female_25_39',
        defaultMessage: 'Female 25-39',
    }),
    female_25_54: defineMessage({
        id: 'female_25_54',
        defaultMessage: 'Female 25-54',
    }),
    female_35_44: defineMessage({
        id: 'female_35_44',
        defaultMessage: 'Female 35-44',
    }),
    female_35_54: defineMessage({
        id: 'female_35_54',
        defaultMessage: 'Female 35-54',
    }),
    female_35_64: defineMessage({
        id: 'female_35_64',
        defaultMessage: 'Female 35-64',
    }),
    female_40_54: defineMessage({
        id: 'female_40_54',
        defaultMessage: 'Female 40-54',
    }),
    female_45_54: defineMessage({
        id: 'female_45_54',
        defaultMessage: 'Female 45-54',
    }),
    female_55_64: defineMessage({
        id: 'female_55_64',
        defaultMessage: 'Female 55-64',
    }),
    female_55_plus: defineMessage({
        id: 'female_55_plus',
        defaultMessage: 'Female 55+',
    }),
    female_65_plus: defineMessage({
        id: 'female_65_plus',
        defaultMessage: 'Female 65+',
    }),
    male_18_plus: defineMessage({
        id: 'male_18_plus',
        defaultMessage: 'Male 18+',
    }),
    male_21_plus: defineMessage({
        id: 'male_21_plus',
        defaultMessage: 'Male 21+',
    }),
    male_18_24: defineMessage({
        id: 'male_18_24',
        defaultMessage: 'Male 18-24',
    }),
    male_18_34: defineMessage({
        id: 'male_18_34',
        defaultMessage: 'Male 18-34',
    }),
    male_18_39: defineMessage({
        id: 'male_18_39',
        defaultMessage: 'Male 18-39',
    }),
    male_18_49: defineMessage({
        id: 'male_18_49',
        defaultMessage: 'Male 18-49',
    }),
    male_25_34: defineMessage({
        id: 'male_25_34',
        defaultMessage: 'Male 25-34',
    }),
    male_25_39: defineMessage({
        id: 'male_25_39',
        defaultMessage: 'Male 25-39',
    }),
    male_25_54: defineMessage({
        id: 'male_25_54',
        defaultMessage: 'Male 25-54',
    }),
    male_35_44: defineMessage({
        id: 'male_35_44',
        defaultMessage: 'Male 35-44',
    }),
    male_35_54: defineMessage({
        id: 'male_35_54',
        defaultMessage: 'Male 35-54',
    }),
    male_35_64: defineMessage({
        id: 'male_35_64',
        defaultMessage: 'Male 35-64',
    }),
    male_40_54: defineMessage({
        id: 'male_40_54',
        defaultMessage: 'Male 40-54',
    }),
    male_45_54: defineMessage({
        id: 'male_45_54',
        defaultMessage: 'Male 45-54',
    }),
    male_55_64: defineMessage({
        id: 'male_55_64',
        defaultMessage: 'Male 55-64',
    }),
    male_55_plus: defineMessage({
        id: 'male_55_plus',
        defaultMessage: 'Male 55+',
    }),
    male_65_plus: defineMessage({
        id: 'male_65_plus',
        defaultMessage: 'Male 65+',
    }),

    // ===================================================
    // ====== AU Podcast Advanced Demographics ============
    // ===================================================
    parent_wchild_less_18: defineMessage({
        id: 'parent_wchild_less_18',
        defaultMessage: 'Parent w/ child <18',
    }),
    mom_wchild_less_18: defineMessage({
        id: 'mom_wchild_less_18',
        defaultMessage: 'Moms',
    }),
    hhi_0_50k: defineMessage({
        id: 'hhi_0_50k',
        defaultMessage: '<$50k HHI',
    }),
    hhi_50k_70k: defineMessage({
        id: 'hhi_50k_70k',
        defaultMessage: '$50 <$70k HHI',
    }),
    hhi_70_100k: defineMessage({
        id: 'hhi_70_100k',
        defaultMessage: '$70 <$100k HHI',
    }),
    hhi_100k_plus: defineMessage({
        id: 'hhi_100k_plus',
        defaultMessage: 'HHI $100K+',
    }),
    hhi_100_150k: defineMessage({
        id: 'hhi_100_150k',
        defaultMessage: '$100 <$150k HHI',
    }),
    hhi_150_200k: defineMessage({
        id: 'hhi_150_200k',
        defaultMessage: '$150 <$200k HHI',
    }),
    hhi_150k_plus: defineMessage({
        id: 'hhi_150k_plus',
        defaultMessage: 'HHI $150K+',
    }),
    hhi_200k_plus: defineMessage({
        id: 'hhi_200k_plus',
        defaultMessage: '$200k+ HHI',
    }),
    married: defineMessage({
        id: 'married',
        defaultMessage: 'Married',
    }),
    single: defineMessage({
        id: 'single',
        defaultMessage: 'Single',
    }),
    empl_fulltime: defineMessage({
        id: 'empl_fulltime',
        defaultMessage: 'Full time employment',
    }),
    empl_homeduties_fulltimeparent_caregiver: defineMessage({
        id: 'empl_homeduties_fulltimeparent_caregiver',
        defaultMessage: 'Homeduties/caregiver employment',
    }),
    empl_manager_administrator: defineMessage({
        id: 'empl_manager_administrator',
        defaultMessage: 'Manager/administrator employment',
    }),
    'empl_paraprofessional(e.g._nurse_educationalaide_paralegal_bookkeeper)': defineMessage({
        id: 'empl_paraprofessional',
        defaultMessage: 'Paraprofessional employment',
    }),
    'empl_professional(e.g._doctor_lawyer_professor_certifiedteacher_accountant)': defineMessage({
        id: 'empl_professional',
        defaultMessage: 'Professional employment',
    }),
    empl_casual_part_time: defineMessage({
        id: 'empl_casual_part_time',
        defaultMessage: 'Casual/part-time employment',
    }),
    empl_trades_labourers: defineMessage({
        id: 'empl_trades_labourers',
        defaultMessage: 'Trades/labourers employment',
    }),
    small_medium_business_owner: defineMessage({
        id: 'small_medium_business_owner',
        defaultMessage: 'Small - medium business owner',
    }),
    senior_decision_maker: defineMessage({
        id: 'senior_decision_maker',
        defaultMessage: 'Senior decision maker',
    }),
    student_apprentice: defineMessage({
        id: 'student_apprentice',
        defaultMessage: 'Student/apprentice',
    }),
    sports_avidfan: defineMessage({
        id: 'sports_avidfan',
        defaultMessage: 'Sports avid fan',
    }),
    afl_avidfan: defineMessage({
        id: 'afl_avidfan',
        defaultMessage: 'AFL avid fan',
    }),
    nrl_avidfan: defineMessage({
        id: 'nrl_avidfan',
        defaultMessage: 'NRL avid fan',
    }),
    horse_racing_avidfan: defineMessage({
        id: 'horse_racing_avidfan',
        defaultMessage: 'Horse racing avid fan',
    }),
    cricket_avidfan: defineMessage({
        id: 'cricket_avidfan',
        defaultMessage: 'Cricket avid fan',
    }),
    netball_avidfan: defineMessage({
        id: 'netball_avidfan',
        defaultMessage: 'Netball avid fan',
    }),
    basketball_avidfan: defineMessage({
        id: 'basketball_avidfan',
        defaultMessage: 'Basketball avid fan',
    }),
    motorsports_avidfan: defineMessage({
        id: 'motorsports_avidfan',
        defaultMessage: 'Motorsports avid fan',
    }),
    golf_avidfan: defineMessage({
        id: 'golf_avidfan',
        defaultMessage: 'Golf avid fan',
    }),
    tennis_avidfan: defineMessage({
        id: 'tennis_avidfan',
        defaultMessage: 'Tennis avid fan',
    }),
    nfl_avidfan: defineMessage({
        id: 'nfl_avidfan',
        defaultMessage: 'NFL avid fan',
    }),
    soccer_avidfan: defineMessage({
        id: 'soccer_avidfan',
        defaultMessage: 'Soccer avid fan',
    }),
    olympics_avidfan: defineMessage({
        id: 'olympics_avidfan',
        defaultMessage: 'Olympics avid fan',
    }),
    cooking_p2m: defineMessage({
        id: 'cooking_p2m',
        defaultMessage: 'Cooking - past month',
    }),
    gardening_p2m: defineMessage({
        id: 'gardening_p2m',
        defaultMessage: 'Gardening - past month',
    }),
    painting_p2m: defineMessage({
        id: 'painting_p2m',
        defaultMessage: 'Painting/sculpture - past month',
    }),
    photography_p2m: defineMessage({
        id: 'photography_p2m',
        defaultMessage: 'Photography - past month',
    }),
    home_renovations_p2m: defineMessage({
        id: 'home_renovations_p2m',
        defaultMessage: 'Home renovation - past month',
    }),
    reading_p2m: defineMessage({
        id: 'reading_p2m',
        defaultMessage: 'Reading - past month',
    }),
    cars_p2m: defineMessage({
        id: 'cars_p2m',
        defaultMessage: 'Auto work - past month',
    }),
    fitness_p2m: defineMessage({
        id: 'fitness_p2m',
        defaultMessage: 'Fitness classes - past month',
    }),
    new_tech_p2m: defineMessage({
        id: 'new_tech_p2m',
        defaultMessage: 'New technology - past month',
    }),
    arts_p2m: defineMessage({
        id: 'arts_p2m',
        defaultMessage: 'Arts/theatre - past month',
    }),
    outdoor_p2m: defineMessage({
        id: 'outdoor_p2m',
        defaultMessage: 'Outdoor rec land - past month',
    }),
    outdoor_water_p2m: defineMessage({
        id: 'outdoor_water_p2m',
        defaultMessage: 'Outdoor rec water - past month',
    }),

    // ===================================================
    // ============= AU Podcast Media ====================
    // ===================================================
    'podcasts_<weekly': defineMessage({
        id: 'podcasts_<weekly',
        defaultMessage: 'Light podcast (< weekly)',
    }),
    'podcasts_<5hrs_wk': defineMessage({
        id: 'podcasts_<5hrs_wk',
        defaultMessage: 'Medium podcast (<5 hrs/week)',
    }),
    'podcasts_5+hrs_wk': defineMessage({
        id: 'podcasts_5+hrs_wk',
        defaultMessage: 'Heavy podcast (5+ hrs/week)',
    }),
    first_listen_past_year: defineMessage({
        id: 'first_listen_past_year',
        defaultMessage: 'First listen podcasts - past year',
    }),
    first_listen_past_2_5_yrs: defineMessage({
        id: 'first_listen_past_2_5_yrs',
        defaultMessage: 'First listen podcasts - past 2-5 years',
    }),
    first_listen_past_5_yrs_plus: defineMessage({
        id: 'first_listen_past_5_yrs_plus',
        defaultMessage: 'First listen podcasts - past 5+ years',
    }),
    newspapers_pm: defineMessage({
        id: 'newspapers_pm',
        defaultMessage: 'Newspapers - past month',
    }),
    magazines_pm: defineMessage({
        id: 'magazines_pm',
        defaultMessage: 'Magazines - past month',
    }),
    radio_streamed_pm: defineMessage({
        id: 'radio_streamed_pm',
        defaultMessage: 'Radio streamed - past month',
    }),
    music_streaming_pm: defineMessage({
        id: 'music_streaming_pm',
        defaultMessage: 'Music streaming service - past month',
    }),
    audio_books_pm: defineMessage({
        id: 'audio_books_pm',
        defaultMessage: 'Audio books - past month',
    }),
    newspapers_pw: defineMessage({
        id: 'newspapers_pw',
        defaultMessage: 'Newspapers - past week',
    }),
    magazines_pw: defineMessage({
        id: 'magazines_pw',
        defaultMessage: 'Magazines - past week',
    }),
    am_fm_dab_radio_pw: defineMessage({
        id: 'am_fm_dab_radio_pw',
        defaultMessage: 'AM/FM/DAB+ radio - past week',
    }),
    radio_streamed_pw: defineMessage({
        id: 'radio_streamed_pw',
        defaultMessage: 'Radio streamed - past week',
    }),
    you_tube_pw: defineMessage({
        id: 'you_tube_pw',
        defaultMessage: 'YouTube - past week',
    }),
    tv_commercial_broadcast_pw: defineMessage({
        id: 'tv_commercial_broadcast_pw',
        defaultMessage: 'TV commercial broadcast - past week',
    }),
    tv_abc_sbs_pw: defineMessage({
        id: 'tv_abc_sbs_pw',
        defaultMessage: 'TV ABC/SBS - past week',
    }),
    tv_catch_up_pw: defineMessage({
        id: 'tv_catch_up_pw',
        defaultMessage: 'TV catch up - past week',
    }),
    pay_tv_pw: defineMessage({
        id: 'pay_tv_pw',
        defaultMessage: 'Pay TV - past week',
    }),
    music_streaming_pw: defineMessage({
        id: 'music_streaming_pw',
        defaultMessage: 'Music streaming services - past week',
    }),
    social_media_pw: defineMessage({
        id: 'social_media_pw',
        defaultMessage: 'Social media - past week',
    }),
    online_news_pw: defineMessage({
        id: 'online_news_pw',
        defaultMessage: 'Online news - past week',
    }),
    audio_books_pw: defineMessage({
        id: 'audio_books_pw',
        defaultMessage: 'Audio books - past week',
    }),
    non_listener_am_fm_dab_radio_pm: defineMessage({
        id: 'non_listener_am_fm_dab_radio_pm',
        defaultMessage: 'Non radio listener - past month',
    }),

    // ===================================================
    // ============= AU Podcast Purchase =================
    // ===================================================
    purchase_sell_home_ny: defineMessage({
        id: 'purchase_sell_home_ny',
        defaultMessage: 'Buy/sell home - intent',
    }),
    purchase_investment_prop_ny: defineMessage({
        id: 'purchase_investment_prop_ny',
        defaultMessage: 'Buy investment property - intent',
    }),
    switch_open_bank_account_ny: defineMessage({
        id: 'switch_open_bank_account_ny',
        defaultMessage: 'Switch/open bank account - intent',
    }),
    finance_refinance_mortgage_ny: defineMessage({
        id: 'finance_refinance_mortgage_ny',
        defaultMessage: 'Finance/refinance mortgage - intent',
    }),
    financial_advisor_ny: defineMessage({
        id: 'financial_advisor_ny',
        defaultMessage: 'Seek financial advisor - intent',
    }),
    own_home_no_mortage: defineMessage({
        id: 'own_home_no_mortage',
        defaultMessage: 'Own home (no mortgage)',
    }),
    own_home_w_mortage: defineMessage({
        id: 'own_home_w_mortage',
        defaultMessage: 'Own home (w/ mortgage)',
    }),
    rent_home: defineMessage({
        id: 'rent_home',
        defaultMessage: 'Rent home',
    }),
    chemist_pw: defineMessage({
        id: 'chemist_pw',
        defaultMessage: 'Buy from pharmacy - past week',
    }),
    groceries_pw: defineMessage({
        id: 'groceries_pw',
        defaultMessage: 'Buy groceries - past week',
    }),
    take_away_store_pw: defineMessage({
        id: 'take_away_store_pw',
        defaultMessage: 'Takeout from restaurant - past week',
    }),
    take_away_app_pw: defineMessage({
        id: 'take_away_app_pw',
        defaultMessage: 'Takeout from app - past week',
    }),
    casual_dining_pw: defineMessage({
        id: 'casual_dining_pw',
        defaultMessage: 'Casual dining - past week',
    }),
    fine_dining_pw: defineMessage({
        id: 'fine_dining_pw',
        defaultMessage: 'Fine dining - past week',
    }),
    meal_kit_pw: defineMessage({
        id: 'meal_kit_pw',
        defaultMessage: 'Meal kit - past week',
    }),
    primary_grocery_shopper: defineMessage({
        id: 'primary_grocery_shopper',
        defaultMessage: 'Primary grocery shopper',
    }),
    hardware_store_pm: defineMessage({
        id: 'hardware_store_pm',
        defaultMessage: 'Buy from hardware store - past month',
    }),
    garden_nursery_pm: defineMessage({
        id: 'garden_nursery_pm',
        defaultMessage: 'Buy from garden/nursery - past month',
    }),
    clothing_fashion_pm: defineMessage({
        id: 'clothing_fashion_pm',
        defaultMessage: 'Buy clothing/fashion - past month',
    }),
    furniture_pm: defineMessage({
        id: 'furniture_pm',
        defaultMessage: 'Buy furniture - past month',
    }),
    alcohol_pm: defineMessage({
        id: 'alcohol_pm',
        defaultMessage: 'Buy alcohol - past month',
    }),
    anything_online_pm: defineMessage({
        id: 'anything_online_pm',
        defaultMessage: 'Buy anything online - past month',
    }),
    own_car_truck: defineMessage({
        id: 'own_car_truck',
        defaultMessage: 'Own car or truck',
    }),
    have_health_insurance: defineMessage({
        id: 'have_health_insurance',
        defaultMessage: 'Have health insurance',
    }),
    have_credit_card: defineMessage({
        id: 'have_credit_card',
        defaultMessage: 'Have credit card',
    }),
    have_vehicle_insurance: defineMessage({
        id: 'have_vehicle_insurance',
        defaultMessage: 'Have vehicle insurance',
    }),
    have_home_insurance: defineMessage({
        id: 'have_home_insurance',
        defaultMessage: 'Have home insurance',
    }),
    have_pet: defineMessage({
        id: 'have_pet',
        defaultMessage: 'Have pet',
    }),
    video_streaming_subs: defineMessage({
        id: 'video_streaming_subs',
        defaultMessage: 'Video streaming paid subscription',
    }),
    music_streaming_subs: defineMessage({
        id: 'music_streaming_subs',
        defaultMessage: 'Music streaming paid subscription',
    }),
    music_streaming_free: defineMessage({
        id: 'music_streaming_free',
        defaultMessage: 'Music streaming free subscription',
    }),
    whitegoods_appliances_ny: defineMessage({
        id: 'whitegoods_appliances_ny',
        defaultMessage: 'Buy whitegoods/appliances - intent',
    }),
    ac_heater_ny: defineMessage({
        id: 'ac_heater_ny',
        defaultMessage: 'Buy AC/heater - intent',
    }),
    furniture_ny: defineMessage({
        id: 'furniture_ny',
        defaultMessage: 'Buy furniture - intent',
    }),
    audio_visual_eqpmnt_ny: defineMessage({
        id: 'audio_visual_eqpmnt_ny',
        defaultMessage: 'Buy audio visual equipment - intent',
    }),
    holi_travel_dom_ny: defineMessage({
        id: 'holi_travel_dom_ny',
        defaultMessage: 'Holiday travel domestic - intent',
    }),
    holi_travel_int_ny: defineMessage({
        id: 'holi_travel_int_ny',
        defaultMessage: 'Holiday travel international - intent',
    }),
    bus_travel_dom_ny: defineMessage({
        id: 'bus_travel_dom_ny',
        defaultMessage: 'Business travel domestic - intent',
    }),
    bus_travel_int_ny: defineMessage({
        id: 'bus_travel_int_ny',
        defaultMessage: 'Business travel international - intent',
    }),
    gym_memb_eqpmnt_ny: defineMessage({
        id: 'gym_memb_eqpmnt_ny',
        defaultMessage: 'Gym membership/equipment - intent',
    }),
    home_renos_ny: defineMessage({
        id: 'home_renos_ny',
        defaultMessage: 'Home renovations - intent',
    }),
    motor_vehicle_ny: defineMessage({
        id: 'motor_vehicle_ny',
        defaultMessage: 'Buy motor vehicle - intent',
    }),
    home_tech_ny: defineMessage({
        id: 'home_tech_ny',
        defaultMessage: 'Buy home tech products - intent',
    }),
    mobile_phone_ny: defineMessage({
        id: 'mobile_phone_ny',
        defaultMessage: 'Buy mobile phone - intent',
    }),
    new_internet_provider_ny: defineMessage({
        id: 'new_internet_provider_ny',
        defaultMessage: 'New internet provider - intent',
    }),
    new_mobile_provider_ny: defineMessage({
        id: 'new_mobile_provider_ny',
        defaultMessage: 'New wireless provider - intent',
    }),
    new_energy_provider_ny: defineMessage({
        id: 'new_energy_provider_ny',
        defaultMessage: 'New energy provider - intent',
    }),
    new_health_insurance_ny: defineMessage({
        id: 'new_health_insurance_ny',
        defaultMessage: 'New health insurance - intent',
    }),
    new_car_insurance_ny: defineMessage({
        id: 'new_car_insurance_ny',
        defaultMessage: 'New car insurance - intent',
    }),
    qsr_daily: defineMessage({
        id: 'qsr_daily',
        defaultMessage: 'QSR daily',
    }),
    qsr_weekly: defineMessage({
        id: 'qsr_weekly',
        defaultMessage: 'Quick serve restaurant weekly',
    }),
    cinema_heavy: defineMessage({
        id: 'cinema_heavy',
        defaultMessage: 'Cinema heavy (monthly)',
    }),
    cinema_medium: defineMessage({
        id: 'cinema_medium',
        defaultMessage: 'Cinema medium (every 2-3 months)',
    }),
    cinema_light: defineMessage({
        id: 'cinema_light',
        defaultMessage: 'Cinema light (every 4-12 months)',
    }),
    primary_grocery_shopper_w_child: defineMessage({
        id: 'primary_grocery_shopper_w_child',
        defaultMessage: 'Primary grocery shopper w/child',
    }),
    in_person_shoppers: defineMessage({
        id: 'in_person_shoppers',
        defaultMessage: 'In-person shopper',
    }),
    online_shoppers: defineMessage({
        id: 'online_shoppers',
        defaultMessage: 'Online shopper',
    }),
    in_person_online_shoppers: defineMessage({
        id: 'in_person_online_shoppers',
        defaultMessage: 'Online/in person shopper',
    }),

    // ===================================================
    // ================= US Headers ======================
    // ===================================================
    college_grad_plus: defineMessage({
        id: 'college_grad_plus',
        defaultMessage: 'College graduate+',
    }),
    post_grad: defineMessage({
        id: 'post_grad',
        defaultMessage: 'Post graduate',
    }),
    hispanic: defineMessage({
        id: 'hispanic',
        defaultMessage: 'Hispanic',
    }),
    race_black_aa: defineMessage({
        id: 'race_black_aa',
        defaultMessage: 'Black/African American',
    }),
    politics_democ_lean: defineMessage({
        id: 'politics_democ_lean',
        defaultMessage: 'Democrat leaning',
    }),
    politics_repub_lean: defineMessage({
        id: 'politics_repub_lean',
        defaultMessage: 'Republican leaning',
    }),
    business_travel_intent: defineMessage({
        id: 'business_travel_intent',
        defaultMessage: 'Business travel - intent',
    }),
    leisure_travel_intent: defineMessage({
        id: 'leisure_travel_intent',
        defaultMessage: 'Leisure travel - intent',
    }),
    switch_wireless_provider_intent: defineMessage({
        id: 'switch_wireless_provider_intent',
        defaultMessage: 'Switch wireless provider - intent',
    }),
    life_insurance_intent: defineMessage({
        id: 'life_insurance_intent',
        defaultMessage: 'Life insurance - intent',
    }),
    home_auto_insurance_intent: defineMessage({
        id: 'home_auto_insurance_intent',
        defaultMessage: 'Home/auto insurance - intent',
    }),
    buy_new_car_truck_intent: defineMessage({
        id: 'buy_new_car_truck_intent',
        defaultMessage: 'Buy new car/truck - intent',
    }),
    home_improvement_intent: defineMessage({
        id: 'home_improvement_intent',
        defaultMessage: 'Home improvement - intent',
    }),
    small_business_owner: defineMessage({
        id: 'small_business_owner',
        defaultMessage: 'Small business owner',
    }),
    female_25_54_and_parent_wchild_less_18: defineMessage({
        id: 'female_25_54_and_parent_wchild_less_18',
        defaultMessage: 'Moms',
    }),
    cinema_monthly: defineMessage({
        id: 'cinema_monthly',
        defaultMessage: 'Cinema monthly',
    }),
    exercise_gym_pm: defineMessage({
        id: 'exercise_gym_pm',
        defaultMessage: 'Exercise/gym - past month',
    }),
    supplements_vitamins_pm: defineMessage({
        id: 'supplements_vitamins_pm',
        defaultMessage: 'Supplements/vitamins - past month',
    }),
    weightloss_products_pm: defineMessage({
        id: 'weightloss_products_pm',
        defaultMessage: 'Weight loss products - past month',
    }),
    exercise_home_pm: defineMessage({
        id: 'exercise_home_pm',
        defaultMessage: 'Exercise at home - past month',
    }),
    department_store_pm: defineMessage({
        id: 'department_store_pm',
        defaultMessage: 'Department store - past month',
    }),
    bigbox_store_pm: defineMessage({
        id: 'bigbox_store_pm',
        defaultMessage: 'Big box store - past month',
    }),
    fastfashion_store_pm: defineMessage({
        id: 'fastfashion_store_pm',
        defaultMessage: 'Fast fashion store - past month',
    }),
    luxury_boutique_pm: defineMessage({
        id: 'luxury_boutique_pm',
        defaultMessage: 'Luxury boutique - past month',
    }),
    online_shopper: defineMessage({
        id: 'online_shopper',
        defaultMessage: 'Online shopper',
    }),
    inperson_shopper: defineMessage({
        id: 'inperson_shopper',
        defaultMessage: 'In-person shopper',
    }),
    'inv_assets_250k+': defineMessage({
        id: 'inv_assets_250k+',
        defaultMessage: 'Investment assets $250K+',
    }),
} as const;

function buildCharacteristicGroupMap<T extends Record<string, readonly string[]>>(groups: T) {
    const map = new Map<string, keyof T>();

    for (const groupKey of Object.keys(groups) as Array<keyof T>) {
        for (const char of groups[groupKey]) {
            map.set(char, groupKey);
        }
    }

    return map;
}

export const USCharacteristicGroupMap = buildCharacteristicGroupMap(UsDemosPlusCharacteristicGroups);
export const AUCharacteristicGroupMap = buildCharacteristicGroupMap(AuDemosPlusCharacteristicGroups);
export const AUProgramCharacteristicGroupMap = buildCharacteristicGroupMap(AuDemosPlusProgramCharacteristicGroups);

// Helper function to get the group for a characteristic
export function getCharacteristicGroups(
    characteristics: string[],
    reportType: 'demosPlusPodcastAu',
): Record<AuDemosPlusGroups, string[]>;
export function getCharacteristicGroups(
    characteristics: string[],
    reportType: 'demosPlusProgramAu',
): Record<AuDemosPlusProgramGroups, string[]>;
export function getCharacteristicGroups(
    characteristics: string[],
    reportType: 'demosPlusPodcastUs' | 'demosPlusProgramUs',
): Record<UsDemosPlusGroups, string[]>;
export function getCharacteristicGroups(
    characteristics: string[],
    reportType: ReportType,
): Record<string, string[]> | null;
export function getCharacteristicGroups(
    characteristics: string[],
    reportType: ReportType,
): Record<string, string[]> | null {
    switch (reportType) {
        case 'demosPlusPodcastAu': {
            const res: Record<AuDemosPlusGroups, string[]> = {
                demographic: [],
                advancedDemographic: [],
                media: [],
                purchase: [],
            };

            for (const characteristic of characteristics) {
                const group = AUCharacteristicGroupMap.get(characteristic);

                if (group) {
                    res[group].push(characteristic);
                }
            }

            return res;
        }
        case 'demosPlusProgramAu': {
            const res: Record<AuDemosPlusProgramGroups, string[]> = {
                purchaseLifestyle: [],
            };

            for (const characteristic of characteristics) {
                const group = AUProgramCharacteristicGroupMap.get(characteristic);

                if (group) {
                    res[group].push(characteristic);
                }
            }

            return res;
        }
        case 'demosPlusProgramUs':
        case 'demosPlusPodcastUs': {
            const res: Record<UsDemosPlusGroups, string[]> = {
                demographic: [],
                advancedDemographic: [],
                purchaseLifestyle: [],
            };

            for (const characteristic of characteristics) {
                const group = USCharacteristicGroupMap.get(characteristic);

                if (group) {
                    res[group].push(characteristic);
                }
            }

            return res;
        }
        default:
            return null;
    }
}

export function isDemosPlusRows(rows: unknown): rows is DemosPlusRow[] {
    return Array.isArray(rows) && rows.length > 0 && 'characteristics' in rows[0];
}

export function getDemosPlusCharacteristicLabel(key: string): string | undefined {
    if (!(key in DemosPlusHeaderMessageMap)) {
        return undefined;
    }
    // TODO: use intl here?? not a priority now, but should be done to ensure translation for characteristics

    const obj = DemosPlusHeaderMessageMap[key as UsDemoPlusCharacteristicHeader].defaultMessage?.[0] as {
        value?: string | undefined;
    };

    return obj.value;
}

type DemoPlusColumnOption<RowType> = {
    columnOptionString: string[];
    columnOptionObject: [TableColumn<RowType>, { hideWhenNarrow?: boolean; width: number | string | undefined }][];
    columnOptionObjectPdf?: TableColumn<RowType>[];
};

const getDemoPlusColumnOptions = <RowType extends DemosPlusRow>(
    rows: RankerApi['rows'],
    isPdf: boolean,
    reportType?: ReportType,
): DemoPlusColumnOption<RowType> => {
    const columnOptions = getDemoPlusColumnHeader(
        rows as ReportDemosPlusPodcastUs['rows'] | ReportDemosPlusPodcastAu['rows'],
    );
    const columnOptionsLabels = columnOptions.map((e) => e.key);

    if (isPdf) {
        const columnOptionsObjectPdf = columnOptions.map((e) => {
            return getDemosPlusCharacteristicColumn(e.key);
        });

        return {
            columnOptionString: columnOptionsLabels,
            columnOptionObject: [],
            columnOptionObjectPdf: columnOptionsObjectPdf,
        };
    }

    const showColumnTooltip =
        reportType && (reportType === 'demosPlusPodcastAu' || reportType === 'demosPlusPodcastUs');

    const region = reportType === 'demosPlusPodcastUs' ? 'US' : 'AU';

    const columnOptionsObject = columnOptions.map(
        (e) =>
            [
                getDemosPlusCharacteristicColumn(e.key, showColumnTooltip, region),
                { hideWhenNarrow: true, width: 'auto' },
            ] as [
                TableColumn<RankerApi['rows'][number]>,
                { hideWhenNarrow?: boolean; width: number | string | undefined },
            ],
    );

    return {
        columnOptionString: columnOptionsLabels,
        columnOptionObject: columnOptionsObject,
        columnOptionObjectPdf: undefined,
    };
};

export { getDemoPlusColumnOptions };
